<template>
  <el-dialog
      :model-value="modelValue"
      title="Generator — butun yilni bir so'rovda yaratish"
      width="720px"
      align-center
      destroy-on-close
      @update:model-value="$emit('update:modelValue', $event)"
  >
    <p class="fc-toolbar__hint fc-gen-intro">
      Bitta qator + bitta davriylik + bitta yil uchun tanlangan oylarga hodisa yozadi.
      Hisobot davri matnlari 5 tilda avtomatik to'ldiriladi.
    </p>

    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="Ma'lumot turi (qator)" prop="dataset_id">
            <el-select v-model="form.dataset_id" filterable placeholder="Tanlang" style="width: 100%">
              <el-option v-for="d in datasets" :key="d.id" :label="datasetTitle(d)" :value="d.id"/>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Davriylik" prop="periodicity_id">
            <el-select v-model="form.periodicity_id" placeholder="Tanlang" style="width: 100%">
              <el-option
                  v-for="p in periodicities"
                  :key="p.id"
                  :label="periodicityTitle(p)"
                  :value="p.id"
              >
                <span class="fc-opt">
                  <span class="fc-opt__dot" :style="{background: p.color}"/>
                  {{ periodicityTitle(p) }}
                </span>
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="8">
          <el-form-item label="Yil" prop="year">
            <el-input-number
                v-model="form.year"
                :min="yearMin"
                :max="yearMax"
                controls-position="right"
                style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Boshlanish kuni" prop="day_from">
            <el-input-number
                v-model="form.day_from"
                :min="1"
                :max="31"
                :disabled="form.last_day"
                controls-position="right"
                style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Tugash kuni">
            <el-input-number
                v-model="form.day_to"
                :min="1"
                :max="31"
                controls-position="right"
                placeholder="—"
                style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item>
        <el-switch
            v-model="form.last_day"
            active-text="Har oyning oxirgi kuni"
            style="--el-switch-on-color: #16A34A"
        />
        <div class="fc-hint">
          Yoqilsa kun har oyda o'zgaradi: 31.01, 28.02, 31.03 ...
          Statistika e'lon taqvimi aynan shunday tuzilgan. Bu rejimda «Boshlanish kuni» maydoni kerak emas.
        </div>
      </el-form-item>

      <el-alert v-if="invalidMonthsMessage" type="error" :closable="false" show-icon class="fc-alert">
        {{ invalidMonthsMessage }}
      </el-alert>

      <div class="fc-label">Oylar</div>
      <div class="fc-presets">
        <el-button size="small" @click="form.months = [...allMonths]">Hammasi</el-button>
        <el-button size="small" @click="form.months = [...quarterMonths]">Choraklik (1,4,7,10)</el-button>
        <el-button size="small" @click="form.months = []">Tozalash</el-button>
        <span class="fc-presets__count">{{ form.months.length }} ta oy tanlandi</span>
      </div>
      <el-checkbox-group v-model="form.months" class="fc-months">
        <el-checkbox-button v-for="m in 12" :key="m" :value="m">{{ monthLabel(m) }}</el-checkbox-button>
      </el-checkbox-group>
      <div class="fc-hint fc-months__hint">
        Bo'sh qoldirilsa 12 oyning hammasi yoziladi.
      </div>

      <el-form-item label="Hisobot davri matni" prop="report_period_mode">
        <el-select v-model="form.report_period_mode" style="width: 100%">
          <el-option v-for="m in modes" :key="m.value" :label="m.label" :value="m.value"/>
        </el-select>
        <div class="fc-hint">{{ activeModeHint }}</div>
      </el-form-item>

      <template v-if="form.report_period_mode === 'custom'">
        <div class="fc-label">
          O'z matningiz <span class="fc-label__hint">(hamma oyga bir xil yoziladi)</span>
        </div>
        <el-tabs v-model="activeReportLang" class="fc-lang-tabs">
          <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
            <template #label>
              <span class="fc-lang-tab">
                {{ lt.tabLabel }}
                <span class="fc-lang-tab__dot" :class="{'is-filled': !!form.report_period[lt.code]}"/>
              </span>
            </template>
            <el-input
                v-model="form.report_period[lt.code]"
                :placeholder="'Hisobot davri — ' + langLabels[lt.code]"
                clearable
            />
          </el-tab-pane>
        </el-tabs>
        <div class="fc-hint fc-months__hint">
          Bo'sh qoldirilsa matn umuman yozilmaydi («Matnsiz» kabi ishlaydi).
        </div>
      </template>

      <el-collapse class="fc-collapse">
        <el-collapse-item title="Qo'shimcha maydonlar (izoh, havola, tartib)" name="extra">
        <div class="fc-label">Izoh <span class="fc-label__hint">(hamma oyga bir xil)</span></div>
        <el-tabs v-model="activeNoteLang" class="fc-lang-tabs">
          <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
            <template #label>
              <span class="fc-lang-tab">
                {{ lt.tabLabel }}
                <span class="fc-lang-tab__dot" :class="{'is-filled': !!form.note[lt.code]}"/>
              </span>
            </template>
            <el-input
                v-model="form.note[lt.code]"
                type="textarea"
                :rows="3"
                :placeholder="'Izoh — ' + langLabels[lt.code]"
            />
          </el-tab-pane>
        </el-tabs>

        <el-form-item label="Havola">
          <el-input v-model="form.link_url" placeholder="Bo'sh qoldirilsa qator havolasi ishlatiladi" clearable/>
        </el-form-item>

        <el-row :gutter="16">
          <el-col :span="12">
            <div class="fc-label">Tartib raqami</div>
            <el-input-number v-model="form.order_number" controls-position="right" style="width: 100%"/>
          </el-col>
          <el-col :span="12">
            <div class="fc-label">Holati</div>
            <el-switch
                v-model="form.is_active"
                inline-prompt
                active-text="Faol"
                inactive-text="Nofaol"
                style="--el-switch-on-color: #16A34A"
            />
          </el-col>
        </el-row>
        </el-collapse-item>
      </el-collapse>

      <div class="fc-replace" :class="{'is-on': form.replace}">
        <el-switch
            v-model="form.replace"
            active-text="Eskisini o'chirib, noldan yozish"
            style="--el-switch-on-color: #F56C6C"
        />
        <div v-if="form.replace" class="fc-replace__body">
          <b>Diqqat.</b> Bu qator + davriylik + {{ form.year }}-yil bo'yicha
          <b>barcha</b> hodisalar o'chiriladi, keyin yangilari yoziladi.
          Tanlanmagan oylardagi va qo'lda tahrirlangan hodisalar ham yo'qoladi.
        </div>
        <div v-else class="fc-replace__body">
          Mavjud kataklar yangilanadi, yo'qlari yaratiladi.
          <b>Kunni o'zgartirayotgan bo'lsangiz</b> shu rejimni yoqing — aks holda eski kundagi
          badge o'chmaydi va katakda ikkita badge qoladi.
        </div>
      </div>
    </el-form>

    <template #footer>
      <el-button plain :disabled="submitting" @click="$emit('update:modelValue', false)">
        Bekor qilish
      </el-button>
      <el-button
          type="primary"
          :loading="submitting"
          :disabled="!!invalidMonthsMessage"
          @click="onSubmit"
      >Generatsiya qilish</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import api, {
  LANG_TABS,
  LANG_LABELS,
  emptyMultiLang,
  displayLangText,
  monthName,
  lastDayOfMonth,
  hasAnyLang,
  apiErrorMessage,
  REPORT_PERIOD_MODES,
  ALL_MONTHS,
  QUARTER_MONTHS,
  YEAR_MIN,
  YEAR_MAX,
} from "./financeCalendarApi";

export default {
  name: "GenerateDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    datasets: { type: Array, default: () => [] },
    periodicities: { type: Array, default: () => [] },
    // Grid qaysi yilni ko'rsatib turgan bo'lsa — shu yil bilan ochiladi.
    year: { type: Number, default: null },
  },
  emits: ["update:modelValue", "done"],
  data() {
    return {
      submitting: false,
      activeReportLang: "uz_lat",
      activeNoteLang: "uz_lat",
      langTabs: LANG_TABS,
      langLabels: LANG_LABELS,
      modes: REPORT_PERIOD_MODES,
      allMonths: ALL_MONTHS,
      quarterMonths: QUARTER_MONTHS,
      yearMin: YEAR_MIN,
      yearMax: YEAR_MAX,
      form: this.buildForm(),
      rules: {
        dataset_id: [{ required: true, message: "Qatorni tanlang", trigger: "change" }],
        periodicity_id: [{ required: true, message: "Davriylikni tanlang", trigger: "change" }],
        year: [{ required: true, message: "Yilni kiriting", trigger: "blur" }],
      },
    };
  },
  computed: {
    activeModeHint() {
      return this.modes.find((m) => m.value === this.form.report_period_mode)?.hint || "";
    },
    effectiveMonths() {
      return this.form.months.length ? [...this.form.months].sort((a, b) => a - b) : ALL_MONTHS;
    },
    // `last_day` yoqilmagan bo'lsa, backend har oy uchun kunni tekshiradi va
    // birinchi mos kelmagan oyda 400 qaytaradi. Shu holatni oldindan aytamiz.
    invalidMonthsMessage() {
      if (this.form.last_day || !this.form.day_from) return "";
      const bad = this.effectiveMonths.filter(
          (month) => this.form.day_from > lastDayOfMonth(this.form.year, month)
      );
      if (!bad.length) return "";
      const names = bad.map((m) => monthName(m)).join(", ");
      return `${this.form.day_from}-kun bu oylarda yo'q: ${names}. Kunni kichraytiring, bu oylarni ro'yxatdan chiqaring yoki «Har oyning oxirgi kuni» rejimini yoqing.`;
    },
  },
  watch: {
    modelValue(val) {
      if (!val) return;
      this.activeReportLang = "uz_lat";
      this.activeNoteLang = "uz_lat";
      this.form = this.buildForm();
    },
  },
  methods: {
    buildForm() {
      return {
        dataset_id: null,
        periodicity_id: null,
        year: this.year || new Date().getFullYear(),
        day_from: 15,
        day_to: null,
        last_day: false,
        months: [...ALL_MONTHS],
        report_period_mode: "prev_month",
        report_period: emptyMultiLang(),
        note: emptyMultiLang(),
        link_url: "",
        order_number: 0,
        is_active: true,
        replace: false,
      };
    },
    datasetTitle(dataset) {
      const title = displayLangText(dataset.title) || `#${dataset.id}`;
      return dataset.is_active === false ? `${title} (nofaol)` : title;
    },
    periodicityTitle(periodicity) {
      const title = displayLangText(periodicity.title) || periodicity.code;
      return periodicity.is_active === false ? `${title} (nofaol)` : title;
    },
    monthLabel(month) {
      return monthName(month, true);
    },
    onSubmit() {
      if (this.invalidMonthsMessage) {
        ElMessage.error(this.invalidMonthsMessage);
        return;
      }
      this.$refs.formRef.validate((valid) => {
        if (!valid) {
          ElMessage.error("Iltimos, majburiy maydonlarni to'ldiring");
          return;
        }
        if (!this.form.last_day && !this.form.day_from) {
          ElMessage.error("Kunni kiriting yoki «Har oyning oxirgi kuni» rejimini yoqing");
          return;
        }
        if (!this.form.replace) {
          this.runGenerate();
          return;
        }
        ElMessageBox.confirm(
            `${this.form.year}-yil bo'yicha bu qator va davriylikdagi barcha hodisalar o'chiriladi, shu jumladan qo'lda tahrirlanganlari ham. Davom etasizmi?`,
            "Noldan qayta yozish",
            { confirmButtonText: "Ha, qayta yozish", cancelButtonText: "Bekor qilish", type: "warning" }
        )
            .then(() => this.runGenerate())
            .catch(() => {});
      });
    },
    runGenerate() {
      const payload = {
        dataset_id: this.form.dataset_id,
        periodicity_id: this.form.periodicity_id,
        year: Number(this.form.year),
        day_to: this.form.day_to ? Number(this.form.day_to) : null,
        months: this.effectiveMonths,
        report_period_mode: this.form.report_period_mode,
        // Faqat `custom` rejimida ishlatiladi, boshqasida e'tiborga olinmaydi.
        report_period: hasAnyLang(this.form.report_period) ? { ...this.form.report_period } : {},
        note: hasAnyLang(this.form.note) ? { ...this.form.note } : {},
        link_url: this.form.link_url.trim(),
        order_number: Number(this.form.order_number) || 0,
        is_active: this.form.is_active,
        replace: this.form.replace,
      };
      if (this.form.last_day) {
        payload.last_day = true;
      } else {
        payload.day_from = Number(this.form.day_from);
      }

      this.submitting = true;
      api
          .generate(payload)
          .then((result) => {
            ElNotification.success({
              title: "Generatsiya tugadi",
              message: this.resultMessage(result),
              duration: 8000,
            });
            this.$emit("update:modelValue", false);
            this.$emit("done", result);
          })
          .catch((error) => {
            // Generator bitta tranzaksiyada ishlaydi — xato bo'lsa hech narsa
            // yozilmaydi (JAVOBLAR.md C6), shuning uchun "qisman bajarildi"
            // ogohlantirishi kerak emas.
            ElNotification.error({
              title: "Generatsiya bajarilmadi",
              message: `${apiErrorMessage(error)} Hech narsa o'zgarmadi.`,
              duration: 0,
            });
          })
          .finally(() => {
            this.submitting = false;
          });
    },
    resultMessage(result) {
      const created = result?.created ?? 0;
      const updated = result?.updated ?? 0;
      const deleted = result?.deleted ?? 0;
      const affected = result?.affected ?? created + updated;
      const parts = [`${affected} ta oy yozildi (${created} yangi, ${updated} yangilandi)`];
      if (deleted) parts.push(`${deleted} ta eski yozuv o'chirildi`);
      return `${parts.join(", ")}.`;
    },
  },
};
</script>

<style scoped lang="scss">
@use "./finance-calendar.scss";

.fc-gen-intro {
  margin: 0 0 16px;
}

.fc-opt {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
}

.fc-presets {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;

  &__count {
    font-size: 12px;
    color: #8a94a6;
  }
}

.fc-months {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  :deep(.el-checkbox-button__inner) {
    border-radius: 6px;
    border-left: 1px solid var(--el-border-color);
    padding: 8px 12px;
  }
}

.fc-months__hint {
  margin-bottom: 16px;
}

.fc-collapse {
  margin-bottom: 16px;
}

.fc-replace {
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fafbfd;
  transition: border-color 0.15s ease, background 0.15s ease;

  &.is-on {
    border-color: #f8b3b3;
    background: #fef5f5;
  }

  &__body {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.6;
    color: #6b7485;
  }
}
</style>
